// src/Hook/request.ts
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

const rawBaseUrl = process.env.REACT_APP_BASE_URL_API;
const BASE_URL_API = rawBaseUrl
  ? (rawBaseUrl.endsWith('/') ? rawBaseUrl : `${rawBaseUrl}/`)
  : undefined;

const TOKEN_FALLBACK = process.env.REACT_APP_TOKEN_BEARER;

export const api = axios.create({
  baseURL: BASE_URL_API,
});

if (!BASE_URL_API) {
  console.warn("REACT_APP_BASE_URL_API non definita: verranno usati URL relativi all'origin del frontend");
}

api.interceptors.request.use((config) => {
  const raw = localStorage.getItem('token');
  const bearer = raw ? raw.replace(/"/g, '') : TOKEN_FALLBACK;

  if (bearer) {
    config.headers = config.headers ?? {};
    (config.headers as any).Authorization = `Bearer ${bearer}`;
  }
  return config;
});

const redirectToLogin = () => {
  const base = process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '.' ? process.env.PUBLIC_URL : '/';
  const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const target = `${normalizedBase}/login`;
  if (window.location.pathname !== target) {
    window.location.href = target;
  }
};

const forceLogout = () => {
  const { store } = require('../ReduxToolkit/Store');
  const { resetMenu } = require('../ReduxToolkit/Reducers/Layout/LayoutReducer');
  localStorage.removeItem("token");
  localStorage.removeItem("email");
  localStorage.removeItem("login");
  localStorage.removeItem("auth");
  store.dispatch(resetMenu());
  redirectToLogin();
};

const isAuthEndpoint = (config?: AxiosRequestConfig) => {
  const endpoint = config?.url ?? '';
  if (typeof endpoint !== 'string') return false;
  try {
    const urlWithoutQuery = endpoint.split('?')[0] ?? endpoint;
    return /\/login$/i.test(urlWithoutQuery);
  } catch {
    return false;
  }
};

const shouldForceLogout = (payload: any, config?: AxiosRequestConfig) => {
  if (isAuthEndpoint(config)) return false;
  const esito = payload?.esito;
  const createdStatus = payload?.created?.status;
  const status = payload?.status;
  const loginRedirect = payload?.login;

  return (
    (esito === 'KO' && typeof createdStatus === 'string' && createdStatus.toLowerCase() === 'ko') ||
    (typeof status === 'string' && status.toLowerCase() === 'ko' && loginRedirect === '/login')
  );
};

api.interceptors.response.use(
  (response) => {
    const data = response?.data;
    if (shouldForceLogout(response?.data, response?.config)) {
      const message = data?.created?.message ?? data?.message;
      if (message) {
         localStorage.setItem("messageError", message);     
      }
      forceLogout();
    }
    return response;
  },
  (error: AxiosError) => {
    const payload = error.response?.data;
    if (shouldForceLogout(payload, error.response?.config ?? error.config)) {
      const message = (payload as any)?.created?.message ?? (payload as any)?.message;
      if (message) {
        localStorage.setItem("messageError", message);
      }
      forceLogout();
    }
    throw error;
  },
);

export function get<T>(url: string, params?: Record<string, any>) {
  return api.get<T>(url, { params });
}

export function postJson<T>(url: string, data?: unknown, cfg?: AxiosRequestConfig) {
  return api.post<T>(url, data, { headers: { 'Content-Type': 'application/json' }, ...cfg });
}

export function postForm<T>(url: string, form: FormData, cfg?: AxiosRequestConfig) {
  return api.post<T>(url, form, cfg);
}

export function putJson<T>(url: string, data?: unknown, cfg?: AxiosRequestConfig) {
  return api.put<T>(url, data, { headers: { 'Content-Type': 'application/json' }, ...cfg });
}

export function del<T>(url: string, cfg?: AxiosRequestConfig) {
  return api.delete<T>(url, cfg);
}

export async function fetchResource<T>(path: string, params?: Record<string, any>): Promise<T> {
  const res: AxiosResponse<any> = await get<any>(path, params);
  if (res?.data?.data !== undefined) return res.data.data as T;
  if (res?.data?.results !== undefined) return res.data.results as T;
  return res.data as T;
}
